import { Alert } from '@clay-run/glaze/alert';
import { useAppBasePath } from '../../_shared/base-path';
import { useLinkedInCampaigns, useLinkedInStatus, useMetaCampaigns, useMetaStatus } from '../api-client';
import { PlatformCampaignsSection } from './PlatformCampaignsSection';

function ConnectionNote() {
  const status = useMetaStatus();
  if (!status.data) return null;
  const { account, page, isSandbox, problem } = status.data;
  if (!account) return <Alert status="warning">Meta isn't connected: {problem ?? 'unknown problem'}</Alert>;
  const notes = [
    isSandbox && 'This is the Meta sandbox account. Ads here never deliver.',
    !page && 'No Facebook Page is configured (META_PAGE_ID), so ads can’t be created yet.',
    page && !page.usable && 'The configured Facebook Page can’t be used with this token, so only campaigns and ad sets are created.',
  ].filter(Boolean);
  if (notes.length === 0) return null;
  return <Alert status="info">{notes.join(' ')}</Alert>;
}

export function MetaCampaignsSection() {
  const base = useAppBasePath();
  const status = useMetaStatus();
  const connected = status.data?.account != null;
  const campaigns = useMetaCampaigns(connected);
  const account = status.data?.account;

  return (
    <PlatformCampaignsSection
      title="Meta ad account"
      accountLabel={account ? `${account.name} · ${account.currency}` : null}
      note={<ConnectionNote />}
      loading={connected && campaigns.isLoading}
      rows={campaigns.data?.map(c => ({
        id: c.id,
        name: c.name,
        subtitle: `${c.objective.replace('OUTCOME_', '').toLowerCase()} · created ${new Date(c.createdTime).toLocaleDateString()}`,
        status: c.effectiveStatus,
        dailyBudgetUsd: c.dailyBudgetUsd,
        metrics: c.metrics,
        href: `${base}/performance/meta/${c.id}`,
      }))}
    />
  );
}

export function LinkedInCampaignsSection() {
  const base = useAppBasePath();
  const status = useLinkedInStatus();
  const connected = status.data?.account != null;
  const campaigns = useLinkedInCampaigns(connected);
  const account = status.data?.account;

  return (
    <PlatformCampaignsSection
      title="LinkedIn ad account"
      accountLabel={account ? `${account.name} · ${account.currency}` : null}
      note={
        status.data && !account ? (
          <Alert status="warning">LinkedIn isn't connected: {status.data.problem ?? 'unknown problem'}</Alert>
        ) : account?.test ? (
          <Alert status="info">This is a LinkedIn test ad account. Ads here never serve.</Alert>
        ) : null
      }
      loading={connected && campaigns.isLoading}
      rows={campaigns.data?.map(c => ({
        id: c.id,
        name: c.name,
        subtitle: `${c.groupName ?? 'No group'} · ${c.objective.toLowerCase().replace(/_/g, ' ')}`,
        status: c.status,
        dailyBudgetUsd: c.dailyBudgetUsd,
        metrics: c.metrics,
        href: `${base}/performance/linkedin/${c.id}`,
      }))}
    />
  );
}
